/* =============================================
   SENCHADO COLLECTION — dynamic engine
   プロトタイプ prototype/collection-mockup.html のロジックを
   DOM データ属性ベースに移植（クライアントサイド方式）
   ============================================= */
(function () {
  'use strict';

  var ALL_AXES = ['tea_type', 'steaming', 'origin', 'cultivar', 'taste'];

  var GROUP_LABELS = {
    tea_type: '茶種',
    steaming: '蒸し',
    origin: '産地',
    cultivar: '品種',
    taste: '味わい'
  };

  var TASTE_LABELS = {
    '和': '和 — おだやかな調和',
    '香': '香 — 華やかな香り',
    '渋': '渋 — キレのある渋味',
    '旨': '旨 — 濃厚な旨味',
    '嫋': '嫋 — 繊細な余韻'
  };

  // 各軸の値の表示順（カルーセル/ピルの並び）。未収録の値は末尾に出現順で続く
  var VOCAB_ORDER = {
    tea_type: ['煎茶', '玉露', '紅茶', '烏龍茶', '抹茶', 'ほうじ茶', 'フレーバーティー', 'ノンカフェイン', 'その他'],
    steaming: ['浅蒸し', '中蒸し', '深蒸し', '釜炒り'],
    taste: ['和', '香', '渋', '旨', '嫋'],
    // 産地は JIS 都道府県コード順（＝北から南）。2026-08-01 山上さん指摘
    // 「県の並び順を変更したい。例えば北から南」→ 谷本決裁 2026-08-03。
    // ここが空だと orderValues が「出現順」に落ち、コレクションのソート（ベストセラー順）が
    // そのまま県の並びになる＝熊本県が先頭、という理由の説明できない順序になっていた。
    // ⚠️ snippets/senchado-product-card.liquid の vocab_origin と完全に同順・同一であること。
    //    ズレると「地図SVG（JIS順で生成）」と「並び」が食い違う。
    //    tests/senchado-origin-order.test.mjs が両者の一致を検査する。
    origin: [
      '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
      '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
      '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
      '岐阜県', '静岡県', '愛知県', '三重県',
      '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
      '鳥取県', '島根県', '岡山県', '広島県', '山口県',
      '徳島県', '香川県', '愛媛県', '高知県',
      '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
    ],
    cultivar: []
  };

  // 茶種 → コレクションハンドル（navigate モード用）
  var TEA_TYPE_HANDLES = {
    '煎茶': 'sencha',
    '玉露': 'gyokuro',
    '紅茶': 'koucha',
    '烏龍茶': 'oolong',
    '抹茶': 'matcha',
    'ほうじ茶': 'houjicha',
    'フレーバーティー': 'flavor-tea',
    'ノンカフェイン': 'non-caffeine',
    'その他': 'other-tea'
  };

  // 丸枠レール フォールバック円の頭文字（写真の無い茶種/属性）。
  // 基本は値の先頭1字。「その他」だけ意味が伝わる「他」に差し替え（2026-07-13 丸枠レール化）。
  var CAROUSEL_INITIAL_OVERRIDE = { 'その他': '他' };

  function carouselInitial(value) {
    if (!value) return '';
    if (CAROUSEL_INITIAL_OVERRIDE[value]) return CAROUSEL_INITIAL_OVERRIDE[value];
    var chars = Array.prototype.slice.call(value);
    return chars[0] || '';
  }

  // 深リンクの絞り込み値を運ぶクエリパラメータ。
  // メガメニュー/ドロワーの「味わいで選ぶ」が /collections/by-taste?filter=香 の形で送る。
  // 軸はページ側の carousel_mode が決めるので、パラメータ名は軸に依らず1つで足りる。
  var DEEPLINK_PARAM = 'filter';

  function readDeepLinkValue() {
    if (!window.location.search) return null;
    // URLSearchParams 非対応環境では深リンクを無効化するだけ（既存挙動に劣化）
    if (typeof URLSearchParams !== 'function') return null;
    var v = new URLSearchParams(window.location.search).get(DEEPLINK_PARAM);
    return v ? v.trim() : null;
  }

  function carouselLabel(mode, value) {
    if (mode === 'taste') return TASTE_LABELS[value] || value;
    return value;
  }

  // 値を VOCAB_ORDER 順に、未収録は出現順で末尾に
  function orderValues(mode, values) {
    var order = VOCAB_ORDER[mode] || [];
    var inOrder = order.filter(function (v) { return values.indexOf(v) !== -1; });
    var rest = values.filter(function (v) { return order.indexOf(v) === -1; });
    return inOrder.concat(rest);
  }

  function CollectionEngine(root) {
    this.root = root;
    this.grid = root.querySelector('[data-grid]');
    this.carouselTrack = root.querySelector('[data-carousel-track]');
    this.filterCloud = root.querySelector('[data-filter-cloud]');
    this.clearAllBar = root.querySelector('[data-clear-all-bar]');
    this.emptyState = root.querySelector('[data-empty]');
    this.loadingEl = root.querySelector('[data-loading]');
    this.bcSep = root.querySelector('[data-bc-sep]');
    this.bcLeaf = root.querySelector('[data-bc-leaf]');
    this.productCountEl = root.querySelector('[data-product-count]');
    this.visibleCountEl = root.querySelector('[data-visible-count]');

    this.carouselMode = root.dataset.carouselMode;
    this.carouselAction = root.dataset.carouselAction;
    this.activeCarouselValue = root.dataset.activeCarousel || null;
    // URL の ?filter= は buildIndex 後に検証してから採用する（存在しない値で空棚にしないため）
    this.deepLinkValue = readDeepLinkValue();
    // 軸棚（味わい/産地/蒸し）で、その軸の値を持たない商品を棚から外すか
    this.requireCarouselValue = root.dataset.requireCarouselValue === 'true';
    // カテゴリ写真(asset_url)を liquid から受け取る(自前ホスティング)。無ければ {}。
    this.carouselImages = {};
    if (root.dataset.carouselImages) {
      try { this.carouselImages = JSON.parse(root.dataset.carouselImages); } catch (e) { this.carouselImages = {}; }
    }
    // 茶種ナビ 件数（liquid が data-carousel-counts で 値→products_count を注入）。無ければ {}。
    this.carouselCounts = {};
    if (root.dataset.carouselCounts) {
      try { this.carouselCounts = JSON.parse(root.dataset.carouselCounts); } catch (e) { this.carouselCounts = {}; }
    }
    this.collectionHandle = root.dataset.collectionHandle;
    this.sectionId = root.dataset.sectionId;
    this.totalCount = parseInt(root.dataset.totalCount, 10) || 0;
    this.sizes = (root.dataset.sizes || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    this.currentSizeIndex = parseInt(root.dataset.defaultSizeIndex, 10) || 0;

    // filters = 全5軸 − carousel_mode
    this.filterGroups = ALL_AXES.filter(function (a) { return a !== this.carouselMode; }, this);

    this.activeFilters = {};
    this.filterGroups.forEach(function (g) { this.activeFilters[g] = []; }, this);

    this.sortMode = 'default';
    this.products = [];

    this.init();
  }

  CollectionEngine.prototype.init = function () {
    var self = this;
    var renderedCards = this.grid.querySelectorAll('[data-card]').length;

    // 入荷順の棚（sections/senchado-collection.liquid の設定「入荷の新しい順に並べる」）は
    // paginate を使わず1回で全件出す。paginate しない for ループはプラットフォーム側で
    // 走査窓に切られる（指定は 50 だが保証値ではない）ので、棚が窓を超えた日に
    // 「少ないカードで正常に起動する」＝例外もエラーも出ない形で壊れる。
    // 追加ページの取得はできない（この分岐は ?page を無視して全件返すのでカードが二重になる）。
    // → 取りに行かず、代わりに必ず鳴らす。テーマエディタでは Liquid 側が可視の警告も出す。
    // 運用側の見張りは `node scripts/audit-restock-flow.mjs shelf`（コンソールは誰も見ない）。
    var arrivalEl = this.grid.querySelector('[data-arrival-sort]');
    if (arrivalEl) {
      if (renderedCards < this.totalCount && window.console) {
        console.warn(
          '[senchado-collection] 入荷順の棚が走査窓で切れています（並び順が誤ります）: 表示=' + renderedCards +
          ' / コレクション総数=' + this.totalCount +
          '（Liquid の自己申告 overflow=' + (arrivalEl.getAttribute('data-arrival-sort-overflow') || '?') +
          ' / 走査=' + (arrivalEl.getAttribute('data-arrival-sort-scanned') || '?') + '）'
        );
      }
      this.start();
      return;
    }

    if (renderedCards < this.totalCount) {
      // 50件超: 残りページを Section Rendering で取得してから起動
      this.loadingEl && this.loadingEl.classList.add('is-visible');
      this.fetchRemainingPages().then(function () {
        if (self.loadingEl) self.loadingEl.classList.remove('is-visible');
        self.start();
      }).catch(function (err) {
        if (window.console) console.warn('[senchado-collection] 追加ページ取得失敗:', err);
        if (self.loadingEl) self.loadingEl.classList.remove('is-visible');
        self.start();
      });
    } else {
      this.start();
    }
  };

  CollectionEngine.prototype.fetchRemainingPages = function () {
    var self = this;
    // ページ数はサーバー申告値（paginate.pages・Liquidがgrid内に出力）が正。
    // 2026-07-02 実測: paginate by: 50 でも page_size=20 にクランプされる（プラットフォーム挙動）
    // → 50件/ページ仮定だと後半ページが欠落する（道具424件で180件しか出ない事故で発覚）。
    var pagesEl = this.grid.querySelector('[data-paginate-pages]');
    var pages = (pagesEl && parseInt(pagesEl.dataset.paginatePages, 10)) || Math.ceil(this.totalCount / 50);
    var tasks = [];
    for (var p = 2; p <= pages; p++) {
      tasks.push(this.fetchPage(p));
    }
    return Promise.all(tasks).then(function (htmls) {
      var emptyNode = self.emptyState;
      htmls.forEach(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var cards = doc.querySelectorAll('[data-card]');
        cards.forEach(function (card) {
          self.grid.insertBefore(document.importNode(card, true), emptyNode);
        });
      });
    });
  };

  CollectionEngine.prototype.fetchPage = function (page) {
    var url = '/collections/' + encodeURIComponent(this.collectionHandle) +
      '?section_id=' + encodeURIComponent(this.sectionId) + '&page=' + page;
    return fetch(url).then(function (res) {
      return res.ok ? res.text() : '';
    });
  };

  CollectionEngine.prototype.start = function () {
    this.buildIndex();
    this.dropAxislessProducts();
    this.applyDeepLink();
    this.renderCarousel();
    this.renderFilterGroups();
    this.bindEvents();
    this.applyFilters();
    this.root.dataset.engine = 'ready';
  };

  // ---------- AXIS SHELF ----------
  // 軸棚（味わいで選ぶ / 産地で選ぶ / 蒸しで選ぶ）から、その軸の値を持たない商品を外す。
  //
  // 2026-08-03 谷本さん指摘で実測: /collections/by-taste は114枚のうち **42枚(37%)** が
  // どの味わいピルにも当たらなかった（by-steaming 33枚/29%、by-origin 16枚/14%）。
  // 中身は抹茶・フレーバーティー・ほうじ茶が中心で、これは
  // 「味わい表示（甘苦旨3軸＋香りアイコン）はシングルオリジン煎茶専用。煎茶軸の流用はしない」
  // （docs/product-sections-spec.md 2026-05-20 設計判断）の通り、**そもそも物差しが当たらない茶**。
  // データの穴ではなく、棚の構成の問題だった。
  //
  // 「味わいで選ぶ」と名乗る棚に、味わいでは選べない茶が4割混ざっている状態を解消する。
  // 棚の母集団（コレクションの条件）は本番データなので触らず、面の側で落とす。
  CollectionEngine.prototype.dropAxislessProducts = function () {
    if (!this.requireCarouselValue || this.carouselAction !== 'filter') return;
    var mode = this.carouselMode;
    var kept = [];
    this.products.forEach(function (p) {
      if (p[mode]) kept.push(p);
      else if (p.el && p.el.parentNode) p.el.parentNode.removeChild(p.el);
    });
    this.products = kept;
  };

  // ---------- DEEP LINK ----------
  // URL の ?filter=<値> を、この棚のカルーセル軸の初期絞り込みとして採用する。
  // 2026-07-29 谷本さん報告: メガメニュー「味わいで選ぶ」の5本が全部 by-taste の未絞り込み状態に
  // 着地していた（リンクは ?filter= を付けていたが、JS が読んでいなかった）。
  //
  // - filter モード（by-taste / by-origin / by-steaming）専用。
  //   navigate モード（茶種ページ）は棚そのものが切り替わるので深リンクの出番がなく、
  //   ここで値を入れると絞り込まないのに is-active リングだけ点く誤表示になる。
  // - 値は「この棚に実在する値」だけ採用する。棚に無い値をそのまま適用すると空棚に着地するため、
  //   古い/壊れたURLは無視して通常の一覧を出す（何も起きないより空棚の方が事故に見える）。
  //   products は全ページ取得後に組んであるので、ここでの実在判定は棚の全件が対象。
  CollectionEngine.prototype.applyDeepLink = function () {
    if (this.carouselAction !== 'filter') return;
    var value = this.deepLinkValue;
    if (!value) return;
    var mode = this.carouselMode;
    var exists = this.products.some(function (p) { return p[mode] === value; });
    if (!exists) return;
    this.activeCarouselValue = value;
    // 深リンク着地時もパンくず末端に絞り込み値を出す（クリック時と同じ見え方に揃える）
    this.updateBreadcrumbLeaf();
  };

  // 絞り込み状態を URL に反映する（共有・再訪でそのまま復元できるように）。
  // pushState だと戻るボタンで URL だけ変わって表示が追従しないため replaceState を使う。
  CollectionEngine.prototype.syncDeepLinkUrl = function () {
    if (this.carouselAction !== 'filter') return;
    if (typeof URLSearchParams !== 'function' || !window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    if (this.activeCarouselValue) {
      url.searchParams.set(DEEPLINK_PARAM, this.activeCarouselValue);
    } else {
      url.searchParams.delete(DEEPLINK_PARAM);
    }
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  };

  // ---------- INDEX ----------
  CollectionEngine.prototype.buildIndex = function () {
    var cards = this.grid.querySelectorAll('[data-card]');
    this.products = Array.prototype.map.call(cards, function (card) {
      var sizes = {};
      card.querySelectorAll('[data-size-data]').forEach(function (s) {
        sizes[s.dataset.sizeData] = {
          id: s.dataset.id,
          price: s.dataset.price,
          compare: s.dataset.compare || '',
          available: s.dataset.available === 'true',
          url: s.dataset.url || '',
          img: s.dataset.img || '',
          srcset: s.dataset.imgSrcset || ''
        };
      });
      var imgEl = card.querySelector('.senchado-collection__card-img img');
      return {
        el: card,
        imgEl: imgEl,
        originalImg: imgEl ? imgEl.getAttribute('src') : '',
        originalSrcset: imgEl ? imgEl.getAttribute('srcset') || '' : '',
        title: card.dataset.title || '',
        created: parseInt(card.dataset.created, 10) || 0,
        tea_type: card.dataset.tea_type || '',
        steaming: card.dataset.steaming || '',
        origin: card.dataset.origin || '',
        cultivar: card.dataset.cultivar || '',
        taste: card.dataset.taste || '',
        sizes: sizes
      };
    });
    // 初期サイズを反映
    this.applySize();
  };

  // ---------- CAROUSEL ----------
  CollectionEngine.prototype.renderCarousel = function () {
    // 発見UI（カルーセル／絞り込み）を出さない棚では要素が存在しない
    // （sections/senchado-collection.liquid の設定 hide_discovery_ui）。
    // 商品が数件しかない棚では、軸を選ばせるUIが商品より先に画面を占めるだけなので出さない。
    if (!this.carouselTrack) return;
    var self = this;
    var mode = this.carouselMode;
    var items;

    if (this.carouselAction === 'navigate') {
      // 全 vocab 値（茶種ナビ）
      items = (VOCAB_ORDER[mode] || []).slice();
    } else {
      // コレクション内に存在する値のみ
      var present = {};
      this.products.forEach(function (p) { if (p[mode]) present[p[mode]] = true; });
      items = orderValues(mode, Object.keys(present));
    }

    this.carouselTrack.innerHTML = items.map(function (value) {
      var isActive = (value === self.activeCarouselValue);
      // 写真(liquid注入のasset_url)があれば円内に <img>、無ければ淡い背景+頭文字1字の円(フォールバック)
      var imgUrl = (self.carouselImages && self.carouselImages[value]) || '';
      var portrait;
      if (imgUrl) {
        portrait = '<span class="senchado-collection__carousel-portrait">' +
          '<img src="' + escapeAttr(imgUrl) + '" alt="" loading="lazy" decoding="async"></span>';
      } else {
        portrait = '<span class="senchado-collection__carousel-portrait senchado-collection__carousel-portrait--empty">' +
          escapeHtml(carouselInitial(value)) + '</span>';
      }
      // 件数（欠損時は件数行ごと非表示）。値は data-carousel-counts 由来の整数。
      // navigate（茶種ナビ）専用: 各カードは「そのコレクション」へ遷移するので products_count が一致する。
      // filter モードでは同じ値でも「現コレクション内の該当数」ではなく単独コレクション全体数になり
      // ミスリードになるため件数は出さない（例: ocha の茶種フィルタ pill）。
      var cnt = (self.carouselAction === 'navigate' && self.carouselCounts) ? self.carouselCounts[value] : undefined;
      var countHtml = (typeof cnt === 'number' && cnt > 0)
        ? '<span class="senchado-collection__carousel-count">' + escapeHtml(String(cnt)) + '点</span>'
        : '';
      return '<button type="button" class="senchado-collection__carousel-card' + (isActive ? ' is-active' : '') +
        '" data-carousel-value="' + escapeAttr(value) + '">' +
        portrait +
        '<span class="senchado-collection__carousel-name">' + escapeHtml(carouselLabel(mode, value)) + '</span>' +
        countHtml +
        '</button>';
    }).join('');
  };

  CollectionEngine.prototype.clickCarousel = function (value) {
    if (this.carouselAction === 'navigate') {
      var handle = TEA_TYPE_HANDLES[value];
      if (handle && handle !== this.collectionHandle) {
        window.location.href = '/collections/' + handle;
        return;
      }
      // 専用ページが無い/自分自身 → トグルフィルタ的挙動
    }
    this.activeCarouselValue = (this.activeCarouselValue === value) ? null : value;
    this.renderCarousel();
    this.updateBreadcrumbLeaf();
    this.syncDeepLinkUrl();
    this.applyFilters();
  };

  CollectionEngine.prototype.updateBreadcrumbLeaf = function () {
    // navigate ページ（茶種）の leaf は Liquid 側で固定。filter ページのみ JS が更新
    if (!this.bcLeaf || this.carouselAction !== 'filter') return;
    if (this.activeCarouselValue) {
      this.bcLeaf.textContent = this.activeCarouselValue;
      if (this.bcSep) this.bcSep.hidden = false;
    } else {
      this.bcLeaf.textContent = '';
      if (this.bcSep) this.bcSep.hidden = true;
    }
  };

  // ---------- FILTER GROUPS ----------
  CollectionEngine.prototype.renderFilterGroups = function () {
    // 発見UIを出さない棚（hide_discovery_ui）では要素が無い。→ renderCarousel と同じ理由。
    if (!this.filterCloud) return;
    // 2026-07-02 谷本FB: アコーディオン(押して開く)を廃止。各軸を「ラベル｜横スクロールピル｜クリア」の
    // 1行に常時展開（縦に伸ばさない・産地47個等は行内で横スクロール）。
    this.filterCloud.innerHTML = this.filterGroups.map(function (group) {
      var label = GROUP_LABELS[group] || group;
      return '<div class="senchado-collection__filter-row" data-group="' + group + '">' +
        '<span class="senchado-collection__filter-rowlabel">' + label + '</span>' +
        '<div class="senchado-collection__filter-pills" data-pills="' + group + '"></div>' +
        '<button type="button" class="senchado-collection__filter-rowclear" data-clear-group="' + group + '">クリア</button>' +
        '</div>';
    }).join('');
  };

  // ---------- FILTER LOGIC ----------
  CollectionEngine.prototype.getBaseProducts = function () {
    var products = this.products;
    if (this.activeCarouselValue && this.carouselAction === 'filter') {
      var mode = this.carouselMode;
      var val = this.activeCarouselValue;
      products = products.filter(function (p) { return p[mode] === val; });
    }
    return products;
  };

  CollectionEngine.prototype.hasAnyFilter = function () {
    return this.filterGroups.some(function (g) { return (this.activeFilters[g] || []).length > 0; }, this);
  };

  CollectionEngine.prototype.applyFilters = function () {
    var self = this;
    var base = this.getBaseProducts();
    var filtered = base.filter(function (p) {
      return self.filterGroups.every(function (group) {
        var vals = self.activeFilters[group] || [];
        if (vals.length === 0) return true;
        return vals.indexOf(p[group]) !== -1;
      });
    });

    this.renderCards(base, filtered);
    this.renderFilterPills(base, filtered);
    this.updateBadges();

    if (this.clearAllBar) this.clearAllBar.classList.toggle('is-visible', this.hasAnyFilter());
    if (this.productCountEl) this.productCountEl.textContent = filtered.length + '件の商品';
    if (this.visibleCountEl) this.visibleCountEl.textContent = filtered.length + '件表示';
    if (this.emptyState) this.emptyState.classList.toggle('is-visible', filtered.length === 0);
  };

  // ---------- CARDS (show/hide + sort) ----------
  CollectionEngine.prototype.renderCards = function (base, filtered) {
    var shown = new Set(filtered);

    // 表示/非表示
    this.products.forEach(function (p) {
      p.el.classList.toggle('is-hidden', !shown.has(p));
    });

    // ソート（visible のみ order で並べ替え）
    var ordered = filtered.slice();
    if (this.sortMode === 'price-asc') {
      ordered.sort(function (a, b) { return priceVal(a, this.currentSizeIndex) - priceVal(b, this.currentSizeIndex); }.bind(this));
    } else if (this.sortMode === 'price-desc') {
      ordered.sort(function (a, b) { return priceVal(b, this.currentSizeIndex) - priceVal(a, this.currentSizeIndex); }.bind(this));
    } else if (this.sortMode === 'name') {
      ordered.sort(function (a, b) { return a.title.localeCompare(b.title, 'ja'); });
    }
    // 在庫あり優先・売り切れは下部にまとめる（各グループ内は上記ソート順を維持=stable）。
    // 在庫は現在選択中サイズで判定。default含め常に order を付与。
    var sizeIdx = this.currentSizeIndex;
    var availArr = [], soldArr = [];
    ordered.forEach(function (p) {
      var s = p.sizes[sizeIdx] || p.sizes['0'];
      ((s && s.available) ? availArr : soldArr).push(p);
    });
    availArr.concat(soldArr).forEach(function (p, i) { p.el.style.order = i; });
  };

  function priceVal(p, idx) {
    var s = p.sizes[idx] || p.sizes['0'];
    if (!s) return 0;
    return parseInt((s.price || '0').replace(/[^0-9]/g, ''), 10) || 0;
  }

  // ---------- FILTER PILLS ----------
  CollectionEngine.prototype.renderFilterPills = function (base, filtered) {
    // 発見UIを出さない棚（hide_discovery_ui）では要素が無い。→ renderCarousel と同じ理由。
    if (!this.filterCloud) return;
    var self = this;
    this.filterGroups.forEach(function (group) {
      var container = self.filterCloud.querySelector('[data-pills="' + group + '"]');
      if (!container) return;

      var allCounts = {};
      base.forEach(function (p) { if (p[group]) allCounts[p[group]] = (allCounts[p[group]] || 0) + 1; });

      var filteredCounts = {};
      filtered.forEach(function (p) { if (p[group]) filteredCounts[p[group]] = (filteredCounts[p[group]] || 0) + 1; });

      var values = orderValues(group, Object.keys(allCounts));
      var anyFilter = self.hasAnyFilter();

      container.innerHTML = values.map(function (val) {
        var isActive = (self.activeFilters[group] || []).indexOf(val) !== -1;
        var matchCount = filteredCounts[val] || 0;
        var isDimmed = !isActive && matchCount === 0 && anyFilter;
        var label = group === 'taste' ? (TASTE_LABELS[val] || val) : val;
        var countDisplay = anyFilter ? matchCount : (allCounts[val] || 0);
        return '<button type="button" class="senchado-collection__pill' + (isActive ? ' is-active' : '') +
          (isDimmed ? ' is-dimmed' : '') + '" data-filter-group="' + group + '" data-filter-value="' + escapeAttr(val) + '">' +
          escapeHtml(label) + '<span class="senchado-collection__pill-count">' + countDisplay + '</span></button>';
      }).join('');

      var groupEl = self.filterCloud.querySelector('[data-group="' + group + '"]');
      if (groupEl) groupEl.classList.toggle('has-active', (self.activeFilters[group] || []).length > 0);
    });
  };

  CollectionEngine.prototype.updateBadges = function () {
    var self = this;
    this.filterGroups.forEach(function (group) {
      var badge = self.filterCloud.querySelector('[data-badge="' + group + '"]');
      if (!badge) return;
      var count = (self.activeFilters[group] || []).length;
      badge.textContent = count > 0 ? count + '件選択中' : '';
    });
  };

  // ---------- TOGGLE / CLEAR ----------
  CollectionEngine.prototype.toggleFilter = function (group, value) {
    if (!this.activeFilters[group]) this.activeFilters[group] = [];
    var idx = this.activeFilters[group].indexOf(value);
    if (idx === -1) this.activeFilters[group].push(value);
    else this.activeFilters[group].splice(idx, 1);
    this.applyFilters();
  };

  CollectionEngine.prototype.clearGroup = function (group) {
    this.activeFilters[group] = [];
    this.applyFilters();
  };

  CollectionEngine.prototype.clearAll = function () {
    var self = this;
    this.filterGroups.forEach(function (g) { self.activeFilters[g] = []; });
    if (this.carouselAction === 'filter') {
      this.activeCarouselValue = null;
      this.renderCarousel();
      this.updateBreadcrumbLeaf();
      this.syncDeepLinkUrl();
    }
    this.applyFilters();
  };

  // ---------- SIZE ----------
  CollectionEngine.prototype.setSize = function (index) {
    this.currentSizeIndex = index;
    this.applySize();
    // サイズで価格・在庫が変わるため常に再適用(在庫あり優先の並びを更新)
    this.applyFilters();
    var btns = this.root.querySelectorAll('[data-size-btn]');
    Array.prototype.forEach.call(btns, function (b) {
      b.classList.toggle('is-active', parseInt(b.dataset.sizeBtn, 10) === index);
    });
  };

  CollectionEngine.prototype.applySize = function () {
    var idx = this.currentSizeIndex;
    var sizeLabel = this.sizes[idx] || '';
    this.products.forEach(function (p) {
      var s = p.sizes[idx] || p.sizes['0'];
      if (!s) return;
      var priceEl = p.el.querySelector('[data-price-display]');
      if (priceEl) {
        var html = '';
        if (s.compare) html += '<del>' + escapeHtml(s.compare) + '</del>';
        html += escapeHtml(s.price) + '<span class="senchado-collection__card-price-tax">（税込）</span>';
        priceEl.innerHTML = html;
      }
      var badge = p.el.querySelector('[data-size-badge]');
      if (badge && sizeLabel) badge.textContent = sizeLabel;

      var link = p.el.querySelector('[data-card-link]');
      if (link && s.url) link.setAttribute('href', s.url);

      if (p.imgEl) {
        var src = s.img || p.originalImg;
        var srcset = s.img ? s.srcset : p.originalSrcset;
        if (src) p.imgEl.setAttribute('src', src);
        if (srcset) p.imgEl.setAttribute('srcset', srcset);
        else p.imgEl.removeAttribute('srcset');
      }

      var btn = p.el.querySelector('[data-add-to-cart]');
      if (btn) {
        btn.dataset.variantId = s.id;
        if (btn.dataset.earlyLocked === 'true') {
          btn.disabled = true;
        } else if (s.available) {
          btn.disabled = false;
          if (btn.dataset.state !== 'success' && btn.dataset.state !== 'loading') btn.textContent = 'カートに入れる';
        } else {
          btn.disabled = true;
          btn.textContent = '売り切れ';
        }
      }
    });
  };

  // ---------- CART ----------
  // カート投入は assets/senchado-product-card.js（document 委譲）へ一本化した。
  // ここに再実装すると二重送信になるので戻さないこと（docs/spec-product-card-component.md）。

  // ---------- EVENTS ----------
  CollectionEngine.prototype.bindEvents = function () {
    var self = this;

    this.root.addEventListener('click', function (e) {
      var t;
      if ((t = e.target.closest('[data-carousel-value]'))) {
        self.clickCarousel(t.dataset.carouselValue);
      } else if ((t = e.target.closest('[data-clear-group]'))) {
        e.stopPropagation();
        self.clearGroup(t.dataset.clearGroup);
      } else if ((t = e.target.closest('[data-toggle-group]'))) {
        // 発見UIを出さない棚では filterCloud 自体が無い（この分岐は本来到達しないが、
        // ハンドラは root 委譲なので防御しておく）
        var groupEl = self.filterCloud && self.filterCloud.querySelector('[data-group="' + t.dataset.toggleGroup + '"]');
        if (groupEl) groupEl.classList.toggle('is-open');
      } else if ((t = e.target.closest('[data-filter-group]'))) {
        self.toggleFilter(t.dataset.filterGroup, t.dataset.filterValue);
      } else if ((t = e.target.closest('[data-clear-all]'))) {
        self.clearAll();
      } else if ((t = e.target.closest('[data-size-btn]'))) {
        self.setSize(parseInt(t.dataset.sizeBtn, 10));
      }
      // [data-add-to-cart] はここでは扱わない（senchado-product-card.js の document 委譲）
    });

    var sortEl = this.root.querySelector('[data-sort]');
    if (sortEl) {
      sortEl.addEventListener('change', function () {
        self.sortMode = sortEl.value;
        self.applyFilters();
      });
    }
  };

  // ---------- UTIL ----------
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function escapeAttr(s) { return escapeHtml(s); }

  // ---------- MOBILE CENTER-FOCUS ----------
  // ホバー無し端末の代替: スクロールで viewport 中央に最も近いカードに .is-centered を付与し、
  // CSS(≤767px) でそのカードの CTA を濃く塗る。中央に来るほど"強い色"へ滑らかに遷移(既存transition)。
  // 視覚は CSS の幅メディアでゲート(窓を狭めればプレビュー可)。クラス付与自体は常時(rAFで軽量)。
  function initCenterFocus(root) {
    var grid = root.querySelector('[data-grid]');
    if (!grid) return;
    var ticking = false;
    var raf = window.requestAnimationFrame || function (cb) { return window.setTimeout(cb, 16); };
    function update() {
      ticking = false;
      var cards = grid.querySelectorAll('.senchado-collection__card');
      var vh = window.innerHeight, cy = vh / 2;
      var mobile = window.innerWidth <= 767;
      var R = vh * 0.42, i, c, r, d, t, p; // R=山なりの半幅
      for (i = 0; i < cards.length; i++) {
        c = cards[i];
        // デスクトップ(ホバー担当)や非表示カードは強調値をクリア
        if (!mobile || c.classList.contains('is-hidden')) { c.style.removeProperty('--cta-p'); continue; }
        r = c.getBoundingClientRect();
        if (r.bottom < -40 || r.top > vh + 40) { c.style.setProperty('--cta-p', '0'); continue; }
        // viewport中央からの距離→0..1。同じ行は同じ縦中心=同じ値(全列に効く)
        d = Math.abs((r.top + r.bottom) / 2 - cy);
        t = Math.max(0, 1 - d / R);
        p = t * t * (3 - 2 * t); // smoothstep=山なり(中央でピーク・徐々に増減)
        c.style.setProperty('--cta-p', p.toFixed(3));
      }
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      raf(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  // ---------- BOOT ----------
  function boot() {
    document.querySelectorAll('[data-senchado-collection]').forEach(function (root) {
      if (root.dataset.engineBooted) return;
      root.dataset.engineBooted = '1';
      try {
        new CollectionEngine(root);
        initCenterFocus(root);
      } catch (err) {
        if (window.console) console.error('[senchado-collection]', err);
        root.dataset.engine = 'ready';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/* =============================================
   SENCHADO COLLECTION — 記事引用カード（売り場）
   仕様: docs/spec-uriba-article-quote.md §5.1
   見た目とエンジンの正: prototype/uriba-quote-mockup.html（floor-engine）

   上の CollectionEngine には一切触れない独立モジュール。
   ・素の商品カード [data-card] は DOM に残したまま .is-quoted で伏せ、
     その直前に引用カード [data-quote-card]（data-card を持たない）を挿し、
     order を引き継ぐ。engine の products / filter / sort / size の経路は無傷。
   ・カデンツで 1 枚も出なかった棚だけ、在庫商品ブロックの末尾に M を1枚だけ足す
     （§2.1 決裁9-改・追加型なので素のカードは伏せない）。
   ・発火は「全ページ読み込み完了（data-engine="ready"）」かつ
     「フィルタ/ソート/サイズが初期状態」のときだけ。
     操作が始まったら素のカードへ戻し、以後は再挿入しない（復元なし）。
   ・DOM 生成は createElement + textContent のみ。blog/handle は危険文字拒否+encodeURIComponent、
     記事URLは相対組立、画像は cdn.shopify.com のみ許可。JSON の parse 失敗は
     握りつぶしてそのカードを引用対象から外す。
   ============================================= */
(function () {
  'use strict';

  // ブログハンドル → 売り場に出す名前
  var BLOG_LABELS = {
    recipe: 'レシピ',
    'okashi-ocha': 'お菓子とお茶',
    ttj: 'TOKYO TEA JOURNAL'
  };

  // 危険文字拒否式（実データのhandleはアンダースコア・日本語を含む。URL化は encodeURIComponent で無害化）
  var SLUG_RE = /^[^\s\/<>"'`\\]+$/;
  var IMAGE_SRC_RE = /^(https:)?\/\/cdn\.shopify\.com\//;

  // リズム（§2.1 決裁6'）: 総数ではなくカデンツ。商品カード GAP_MIN〜GAP_MAX 枚ごとに引用 1 枚。
  var GAP_MIN = 4;      // 引用と引用のあいだにはさむ商品カードの最小枚数
  var GAP_MAX = 6;      // 同・最大枚数（この幅をシードで都度揺らす）
  var MAX_ARTICLES = 6; // 1商品あたり読み込む記事の上限（バッチ側も6件で焼く）

  // フォールバック（§2.1 決裁9-改）: 引用の手前に必要な商品カード枚数。
  // 棚の商品がこれ未満なら出さない（記事から始めない＝決裁7と同じ 4 枚）
  var FALLBACK_LEAD = 4;

  var PRODUCT_CAPTION = '登場するお茶';

  // フィルタ/カルーセル/サイズ/クリア = 売り場の並びを変える操作
  var OPERATION_SELECTOR = [
    '[data-carousel-value]',
    '[data-filter-group]',
    '[data-clear-group]',
    '[data-clear-all]',
    '[data-size-btn]'
  ].join(',');

  // ---------- 乱数（プロト floor-engine から移植・決定論） ----------
  function mulberry32(a) {
    a = a >>> 0;
    return function () {
      a = (a + 0x6D2B79F5) >>> 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function hash32(str) {
    var h = 2166136261 >>> 0;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h >>> 0;
  }

  // 日替わりシード: YYYYMMDD + コレクションハンドル（同日は全員同じ並び）
  function dateKey(d) {
    d = d || new Date();
    var m = d.getMonth() + 1;
    var day = d.getDate();
    return String(d.getFullYear()) + (m < 10 ? '0' : '') + m + (day < 10 ? '0' : '') + day;
  }

  function dateSeed(key, handle) {
    return hash32(String(key) + ':' + String(handle || ''));
  }

  // ---------- 値の検証 ----------
  function articleUrl(article) {
    if (!article) return '';
    var blog = article.blog;
    var handle = article.handle;
    if (typeof blog !== 'string' || typeof handle !== 'string') return '';
    if (!SLUG_RE.test(blog) || !SLUG_RE.test(handle)) return '';
    return '/blogs/' + encodeURIComponent(blog) + '/' + encodeURIComponent(handle);
  }

  function imageSrc(url) {
    if (typeof url !== 'string' || !url) return '';
    if (!IMAGE_SRC_RE.test(url)) return '';
    return url.indexOf('//') === 0 ? 'https:' + url : url;
  }

  // 埋込 JSON → 表示に使える記事だけの配列（壊れた値は黙って捨てる）
  function readArticles(card) {
    var node = card.querySelector('script[type="application/json"][data-featured-articles]');
    if (!node) return [];
    var raw;
    try {
      raw = JSON.parse(node.textContent);
    } catch (e) {
      return [];
    }
    if (!raw || Object.prototype.toString.call(raw) !== '[object Array]') return [];

    var out = [];
    for (var i = 0; i < raw.length && out.length < MAX_ARTICLES; i++) {
      var a = raw[i];
      if (!a || typeof a !== 'object') continue;
      var url = articleUrl(a);
      var img = imageSrc(a.image);
      if (!url || !img) continue;
      if (typeof a.title !== 'string' || !a.title) continue;
      out.push({
        url: url,
        image: img,
        title: a.title,
        label: BLOG_LABELS[a.blog] || a.blog
      });
    }
    return out;
  }

  // ---------- 配置計画（純関数・DOM非依存） ----------
  // items: [{ available: bool, eligible: bool, articles: number }]（表示順）
  // → [{ index, size: 'l'|'m', articleIndex, fallback, anchor? }]（index 昇順）
  //
  // 統制ルール（谷本さん実機FB 2026-07-28 = 仕様書 §2.1 決裁6'/7）
  //   a. リズムは総数ではなくカデンツ。商品カード GAP_MIN〜GAP_MAX 枚ごとに引用 1 枚。
  //      ギャップ値はシード決定論で都度引く（4/5/6 の揺らぎ）。
  //   b. 先頭も同じルール。商品を4枚以上見せてから最初の引用（記事から始めない）。
  //   c. 適格商品が近くに無い区間はリズムが伸びてよい。前倒しも詰め込みもしない
  //      （ギャップに達していなければ引用にしない／達したら「次に現れる適格商品」を引用化）。
  //   d. 最初の引用のみ L（入口の平台）・以降は M。
  //   e. 引用同士の隣接禁止は a. から自動的に満たされる（あいだに必ず商品4枚以上）。
  //   f. 適格 = 在庫あり かつ 記事あり。
  //   g. フォールバック（§2.1 決裁9-改）: カデンツで 1 枚も出なかった棚に限り、適格商品が
  //      あれば M を 1 枚だけ置く。位置は在庫商品ブロックの末尾（表示順で最後の在庫あり
  //      カードの直後）。手前の商品が FALLBACK_LEAD 枚に満たなければ「FALLBACK_LEAD 枚目の
  //      直後」まで下げ、棚の商品が FALLBACK_LEAD 枚未満なら出さない。
  //      追加型なので素のカードは伏せない（index = 引用元商品 / anchor = 挿入位置）。
  // 引用に差し替えたカードは商品として数えない（伏せられて売り場から消えるため）。
  function nextGap(rnd) {
    return GAP_MIN + Math.floor(rnd() * (GAP_MAX - GAP_MIN + 1));
  }

  // 引用元の商品と記事をシード列から決める（カデンツと同じ rnd ストリームの続き）
  function planFallback(items, rnd) {
    var n = items.length;
    if (n < FALLBACK_LEAD) return null; // 商品4枚未満の棚は構造的に出さない

    var eligible = [];
    var lastAvailable = -1;
    for (var i = 0; i < n; i++) {
      if (items[i].eligible) eligible.push(i);
      if (items[i].available || items[i].eligible) lastAvailable = i; // 適格 ⊂ 在庫あり
    }
    if (!eligible.length) return null;

    // 在庫ブロックの末尾。手前の商品が足りなければ FALLBACK_LEAD 枚目の直後まで下げる
    var anchor = lastAvailable > FALLBACK_LEAD - 1 ? lastAvailable : FALLBACK_LEAD - 1;
    if (anchor > n - 1) anchor = n - 1;

    var pick = eligible[Math.floor(rnd() * eligible.length)];
    var count = items[pick].articles > 0 ? items[pick].articles : 1;
    return {
      index: pick,
      anchor: anchor,
      size: 'm', // フォールバックは M 固定（L は「入口の平台」専用）
      articleIndex: Math.floor(rnd() * count),
      fallback: true
    };
  }

  function planQuotes(items, seed) {
    var out = [];
    var n = items ? items.length : 0;
    if (!n) return out;

    var rnd = mulberry32(seed);
    var gap = nextGap(rnd);
    var since = 0; // 直前の引用（無ければ売り場の先頭）からの商品カード枚数

    for (var s = 0; s < n; s++) {
      if (since >= gap && items[s].eligible) {
        var count = items[s].articles > 0 ? items[s].articles : 1;
        out.push({
          index: s,
          size: out.length === 0 ? 'l' : 'm',
          articleIndex: Math.floor(rnd() * count),
          fallback: false
        });
        since = 0;
        gap = nextGap(rnd);
      } else {
        since++;
      }
    }

    if (!out.length) {
      var extra = planFallback(items, rnd);
      if (extra) out.push(extra);
    }

    return out;
  }

  // ---------- カード生成（createElement + textContent のみ） ----------
  function el(doc, tag, className) {
    var node = doc.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  function buildQuoteCard(card, article, size, streamIndex) {
    if (!article || !article.url || !article.image) return null;
    var doc = card.ownerDocument;
    var link = card.querySelector('[data-card-link]');
    var href = link ? link.getAttribute('href') : '';
    if (!href) return null;

    var node = el(doc, 'div', 'senchado-collection__quote');
    node.setAttribute('data-quote-card', '');
    node.setAttribute('data-quote-size', size === 'l' ? 'l' : 'm');
    node.setAttribute('data-quote-index', String(streamIndex));

    // --- 記事へ（写真＋ブログ名＋タイトル） ---
    var artLink = el(doc, 'a', 'senchado-collection__quote-article');
    artLink.setAttribute('href', article.url);

    var artFig = el(doc, 'div', 'senchado-collection__quote-img');
    var artImg = el(doc, 'img');
    artImg.setAttribute('src', article.image);
    artImg.setAttribute('alt', ''); // タイトルが直下にテキストで出るため装飾扱い
    artImg.setAttribute('loading', 'lazy');
    artImg.setAttribute('decoding', 'async');
    artFig.appendChild(artImg);
    artLink.appendChild(artFig);

    var label = el(doc, 'div', 'senchado-collection__quote-label');
    label.textContent = article.label || '';
    artLink.appendChild(label);

    var title = el(doc, 'div', 'senchado-collection__quote-title');
    title.textContent = article.title;
    artLink.appendChild(title);
    node.appendChild(artLink);

    // --- 商品へ（サムネ＋商品名＋価格） ---
    var prodLink = el(doc, 'a', 'senchado-collection__quote-product');
    prodLink.setAttribute('href', href);

    var cardImg = card.querySelector('.senchado-collection__card-img img');
    if (cardImg) {
      var thumb = el(doc, 'div', 'senchado-collection__quote-thumb');
      var thumbImg = el(doc, 'img');
      thumbImg.setAttribute('src', cardImg.getAttribute('src') || '');
      var srcset = cardImg.getAttribute('srcset');
      if (srcset) thumbImg.setAttribute('srcset', srcset);
      thumbImg.setAttribute('sizes', '56px');
      thumbImg.setAttribute('alt', '');
      thumbImg.setAttribute('loading', 'lazy');
      thumbImg.setAttribute('decoding', 'async');
      thumb.appendChild(thumbImg);
      prodLink.appendChild(thumb);
    }

    var meta = el(doc, 'div', 'senchado-collection__quote-meta');
    var cap = el(doc, 'div', 'senchado-collection__quote-cap');
    cap.textContent = PRODUCT_CAPTION;
    meta.appendChild(cap);

    var name = el(doc, 'div', 'senchado-collection__quote-name');
    name.textContent = card.dataset.title || '';
    meta.appendChild(name);

    var priceText = quotePrice(card);
    if (priceText) {
      var price = el(doc, 'div', 'senchado-collection__quote-price');
      price.textContent = priceText;
      meta.appendChild(price);
    }

    prodLink.appendChild(meta);
    node.appendChild(prodLink);
    return node;
  }

  function sizeData(card) {
    return card.querySelector('[data-size-data="0"]') || card.querySelector('[data-size-data]');
  }

  function quotePrice(card) {
    var s = sizeData(card);
    return (s && s.dataset.price) ? s.dataset.price : '';
  }

  function isAvailable(card) {
    var s = sizeData(card);
    if (s) return s.dataset.available === 'true';
    return !card.querySelector('.senchado-collection__card-soldout');
  }

  // ---------- ストリーム（表示順） ----------
  // engine が order で並べ替えるので DOM 順ではなく order 順を売り場の並びとする
  function collectStream(grid) {
    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-card]'));
    var list = [];
    cards.forEach(function (card, domIndex) {
      if (card.classList.contains('is-hidden')) return;
      var order = parseInt(card.style.order, 10);
      list.push({
        card: card,
        order: isNaN(order) ? domIndex : order,
        dom: domIndex
      });
    });
    list.sort(function (a, b) { return (a.order - b.order) || (a.dom - b.dom); });
    return list;
  }

  // ---------- 挿入 / 解除 ----------
  function renderQuotes(root, state) {
    var grid = state.grid;
    var stream = collectStream(grid);
    if (!stream.length) return;

    var items = stream.map(function (entry) {
      var available = isAvailable(entry.card);
      entry.articles = available ? readArticles(entry.card) : [];
      return {
        available: available,
        eligible: entry.articles.length > 0,
        articles: entry.articles.length
      };
    });

    var key = root.dataset.quoteDate || dateKey();
    var plan = planQuotes(items, dateSeed(key, root.dataset.collectionHandle));

    plan.forEach(function (q) {
      var entry = stream[q.index];
      if (!entry) return;
      var slot = q.fallback ? stream[q.anchor] : entry;
      if (!slot) return;
      var article = entry.articles[q.articleIndex] || entry.articles[0];
      var node = buildQuoteCard(entry.card, article, q.size, q.fallback ? q.anchor : q.index);
      if (!node) return;
      // order は差し込み位置のカードから引き継ぐ。同 order 同士は DOM 順で決まるので、
      // カデンツ＝そのカードの直前 / フォールバック＝在庫ブロック末尾カードの直後に出る
      node.style.order = slot.card.style.order || String(slot.order);
      if (q.fallback) {
        // 追加型（決裁9-改）: 素のカードは伏せない。引用を1枚足すだけ
        node.setAttribute('data-quote-fallback', '');
        grid.insertBefore(node, slot.card.nextSibling);
        state.nodes.push(node);
      } else {
        grid.insertBefore(node, slot.card);
        slot.card.classList.add('is-quoted');
        state.nodes.push(node);
        state.cards.push(slot.card);
      }
    });

    if (state.nodes.length) grid.classList.add('has-quotes');
  }

  function clearQuotes(state) {
    state.nodes.forEach(function (node) {
      if (node.parentNode) node.parentNode.removeChild(node);
    });
    state.cards.forEach(function (card) {
      card.classList.remove('is-quoted');
    });
    state.nodes = [];
    state.cards = [];
    if (state.grid) state.grid.classList.remove('has-quotes');
  }

  // フィルタ/ソート/サイズが初期状態か（Codex M4: 読み込み完了前の操作と競合させない）
  function isPristine(root, state) {
    if (state.operated) return false;
    var sort = root.querySelector('[data-sort]');
    if (sort && sort.value && sort.value !== 'default') return false;
    if (root.querySelector('.senchado-collection__pill.is-active')) return false;
    var bar = root.querySelector('[data-clear-all-bar]');
    if (bar && bar.classList.contains('is-visible')) return false;
    return true;
  }

  // ---------- BOOT ----------
  function initQuotes(root) {
    if (root.dataset.quoteBooted) return;
    root.dataset.quoteBooted = '1';

    var grid = root.querySelector('[data-grid]');
    if (!grid) return;

    var state = { grid: grid, operated: false, settled: false, nodes: [], cards: [] };

    function markOperated() {
      if (state.operated) return;
      state.operated = true;
      state.settled = true; // 復元なし（決裁3）
      clearQuotes(state);
    }

    // capture: engine の bubble ハンドラより先に素のカードへ戻す
    root.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest(OPERATION_SELECTOR)) markOperated();
    }, true);
    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('[data-sort]')) markOperated();
    }, true);

    function tryRender() {
      if (state.settled) return true;
      if (root.dataset.engine !== 'ready') return false;
      state.settled = true;
      if (!isPristine(root, state)) return true;
      try {
        renderQuotes(root, state);
      } catch (err) {
        if (window.console) console.warn('[senchado-collection] 記事引用の挿入に失敗:', err);
        clearQuotes(state);
      }
      return true;
    }

    if (!tryRender() && window.MutationObserver) {
      var mo = new MutationObserver(function () {
        if (tryRender()) mo.disconnect();
      });
      mo.observe(root, { attributes: true, attributeFilter: ['data-engine'] });
    }
  }

  function bootQuotes() {
    document.querySelectorAll('[data-senchado-collection]').forEach(initQuotes);
  }

  // テスト用（純ロジックの単体検証・日付固定は root の data-quote-date）
  window.SenchadoCollectionQuote = {
    mulberry32: mulberry32,
    dateKey: dateKey,
    dateSeed: dateSeed,
    articleUrl: articleUrl,
    imageSrc: imageSrc,
    readArticles: readArticles,
    planQuotes: planQuotes
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootQuotes);
  } else {
    bootQuotes();
  }
})();
